import { REAGION_SOATO } from '@/common/constants/regions';
import { MasterApprenticesRepublic, MasterApprenticesRegions } from '../models/master-apprentices.model';

const HEADER_BG = 'FFF3F4F6';
const TOTAL_BG = 'FFF3F4F6';
const ROW_ODD_BG = 'FFF9FAFB';
const ROW_EVEN_BG = 'FFFFFFFF';
const BORDER_COLOR = 'FFD1D5DB';

const border = () => ({
    top: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
    left: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
    bottom: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
    right: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
});

const REPUBLIC_COLUMNS = [
    { key: 'region_name', label: 'Viloyat nomi' },
    { key: 'cotton_yield_under_60', label: "Hosildorlik 60 sentnerdan ko'p klaster" },
    { key: 'masters_count', label: 'F/Xlardan ustozlar soni' },
    { key: 'cotton_yield_above_30', label: 'Hosildorlik 30 sentnerdan kam klaster' },
    { key: 'apprentices_count', label: 'F/Xlardan shogirdlar soni' },
];

const DISTRICT_COLUMNS = [
    { key: 'district_name', label: 'Tumanlar nomi' },
    { key: 'cotton_yield_under_60', label: "Hosildorlik 60 sentnerdan ko'p klaster" },
    { key: 'masters_count', label: 'F/Xlardan ustozlar soni' },
    { key: 'cotton_yield_above_30', label: 'Hosildorlik 30 sentnerdan kam klaster' },
    { key: 'apprentices_count', label: 'F/Xlardan shogirdlar soni' },
];

async function exportTable<T extends Record<string, any>>(
    data: T[],
    columnsDef: { key: string; label: string }[],
    totals: Record<string, number>,
    sheetName: string,
    filename: string,
) {
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(sheetName);

    const totalCols = 1 + columnsDef.length; // # column + data columns

    ws.columns = [
        { width: 6 },
        ...columnsDef.map((col) => ({
            width: col.key.includes('name') ? 28 : 20,
        })),
    ];

    // ─── HEADER ROW ────────────────────────────────────────────────────────────
    const headerValues = ['#', ...columnsDef.map((c) => c.label)];
    const hRow = ws.addRow(headerValues);
    hRow.height = 44;
    hRow.eachCell((cell) => {
        cell.font = { bold: true, size: 10 };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } };
        cell.border = border();
    });

    // ─── DATA ROWS ─────────────────────────────────────────────────────────────
    data.forEach((row, idx) => {
        const rowBg = idx % 2 === 0 ? ROW_EVEN_BG : ROW_ODD_BG;
        const values: (string | number)[] = [
            idx + 1,
            ...columnsDef.map((col) => {
                const val = row[col.key];
                if (col.key.includes('name')) return String(val || '');
                return Number(val || 0);
            }),
        ];
        const excelRow = ws.addRow(values);
        excelRow.height = 22;
        excelRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBg } };
            cell.border = border();
            if (colNumber === 1) {
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.font = { size: 10, color: { argb: 'FF6B7280' } };
            } else if (colNumber === 2) {
                cell.alignment = { horizontal: 'left', vertical: 'middle' };
                cell.font = { bold: true, size: 10 };
            } else {
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.font = { size: 10, color: { argb: 'FF1E40AF' } };
            }
        });
    });

    // ─── TOTALS ROW ────────────────────────────────────────────────────────────
    const totalsValues: (string | number)[] = [
        '',
        'Jami',
        ...columnsDef.slice(1).map((col) => {
            return totals[col.key] ?? 0;
        }),
    ];
    const totalRow = ws.addRow(totalsValues);
    totalRow.height = 24;
    totalRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: TOTAL_BG } };
        cell.border = border();
        cell.font = { bold: true, size: 10 };
        if (colNumber <= 2) {
            cell.alignment = { horizontal: colNumber === 1 ? 'center' : 'left', vertical: 'middle' };
        } else {
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.font = { bold: true, size: 10, color: { argb: 'FF1E40AF' } };
        }
    });

    // Top border for totals
    for (let c = 1; c <= totalCols; c++) {
        const cell = ws.getCell(totalRow.number, c);
        cell.border = {
            ...border(),
            top: { style: 'medium', color: { argb: BORDER_COLOR } },
        };
    }

    // ─── FREEZE PANES ──────────────────────────────────────────────────────────
    ws.views = [{ state: 'frozen', xSplit: 2, ySplit: 1 }];

    // ─── DOWNLOAD ──────────────────────────────────────────────────────────────
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
}

export async function exportTeacherStudentRepublicToExcel(
    data: MasterApprenticesRepublic[],
    totals: Record<string, number>,
    filename = 'ustoz-shogird-respublika',
) {
    // REAGION_SOATO tartibida
    const sorted = [...data].sort((a, b) => {
        const indexA = REAGION_SOATO.findIndex((item) => item.id === a.region_id);
        const indexB = REAGION_SOATO.findIndex((item) => item.id === b.region_id);
        return indexA - indexB;
    });
    await exportTable(sorted, REPUBLIC_COLUMNS, totals, 'Ustoz-shogird respublika', filename);
}

export async function exportTeacherStudentDistrictToExcel(
    data: MasterApprenticesRegions[],
    totals: Record<string, number>,
    regionName?: string,
) {
    const filename = regionName ? `ustoz-shogird-${regionName.toLowerCase()}` : 'ustoz-shogird-viloyat';
    const wsName = regionName ? `Ustoz-shogird ${regionName}` : 'Ustoz-shogird tuman';
    await exportTable(data, DISTRICT_COLUMNS, totals, wsName, filename);
}
